import ICAL from "ical.js";
import { findDepartmentForCode, normalizeCode } from "./departments";
import type { Department, LessonEvent, UploadSummary } from "./types";

type ParseResult = {
  events: LessonEvent[];
  summary: UploadSummary;
};

const codePattern = /\b(?:3dpc|[a-z]{1,5})[a-z0-9-]*\d+[a-z0-9-]*\b|\b3dpc[a-z0-9-]*\b/gi;

export function parseIcalFile(content: string, departments: Department[], fileName: string): ParseResult {
  const parsed = ICAL.parse(content);
  const calendar = new ICAL.Component(parsed);
  const vevents = calendar.getAllSubcomponents("vevent");
  const today = startOfToday();
  const horizon = addMonths(today, 18);

  let missingDateCount = 0;

  const events = vevents.flatMap((component, index) => {
    try {
      const event = new ICAL.Event(component);
      const start = event.startDate?.toJSDate();
      if (!start) {
        missingDateCount += 1;
        return [];
      }

      return expandEvent(event, component, departments, index, today, horizon);
    } catch {
      missingDateCount += 1;
      return [];
    }
  });

  events.sort((a, b) => a.start.getTime() - b.start.getTime());
  const upcomingCount = events.filter((event) => event.start >= today).length;
  const pastCount = events.length - upcomingCount;

  return {
    events,
    summary: {
      fileName,
      importedCount: upcomingCount,
      ignoredPastCount: pastCount,
      totalCount: events.length,
      missingDateCount
    }
  };
}

export function rematchDepartments(events: LessonEvent[], departments: Department[]) {
  return events.map((event) => ({
    ...event,
    department: findDepartmentForCode(event.code, departments)
  }));
}

function expandEvent(event: ICAL.Event, component: ICAL.Component, departments: Department[], index: number, today: Date, horizon: Date) {
  if (!event.isRecurring()) {
    return [toLessonEvent(event, component, departments, index, event.startDate.toJSDate(), event.endDate?.toJSDate() ?? null)];
  }

  const lessons: LessonEvent[] = [];
  const iterator = event.iterator(event.startDate);
  let next: ICAL.Time | null;
  let guard = 0;

  while ((next = iterator.next() as ICAL.Time | null) && guard < 500) {
    guard += 1;
    const details = event.getOccurrenceDetails(next);
    const start = details.startDate.toJSDate();
    if (start >= horizon) break;
    lessons.push(toLessonEvent(details.item, details.item.component, departments, index, start, details.endDate.toJSDate()));
  }

  return lessons;
}

function toLessonEvent(event: ICAL.Event, component: ICAL.Component, departments: Department[], index: number, start: Date, end: Date | null): LessonEvent {
  const fields = collectFields(component, event);
  const summaryParts = parseSummary(event.summary || "");
  const code = summaryParts.moduleCode || extractCode(fields, departments);
  const department = findDepartmentForCode(code, departments);

  return {
    id: `${event.uid || event.summary || "event"}-${index}-${start.toISOString()}`,
    summary: event.summary || "Untitled lesson",
    moduleCode: code,
    topic: summaryParts.topic || event.summary || "Untitled lesson",
    delivery: summaryParts.delivery,
    start,
    end,
    location: event.location || "",
    description: event.description || "",
    teacher: extractTeacher(fields),
    code,
    sourceFields: fields,
    department
  };
}

function collectFields(component: ICAL.Component, event: ICAL.Event) {
  const categories = component.getFirstPropertyValue("categories");
  const className = component.getFirstPropertyValue("class");
  const fields = [
    event.summary,
    event.location,
    event.description,
    event.organizer?.toString(),
    stringifyValue(categories),
    stringifyValue(className)
  ];

  component.getAllProperties().forEach((property) => {
    const name = property.name.toLowerCase();
    if (name.startsWith("x-")) fields.push(stringifyValue(property.getFirstValue()));
  });

  return fields.filter(Boolean).map((field) => String(field).replace(/\s+/g, " ").trim());
}

function stringifyValue(value: unknown) {
  if (Array.isArray(value)) return value.join(" ");
  return value == null ? "" : String(value);
}

function extractCode(fields: string[], departments: Department[]) {
  const text = fields.join(" ").replace(/[_/]/g, " ");
  const moduleMatch = /Modul\s+([^,]+)/i.exec(text);
  if (moduleMatch) return normalizeModuleCode(moduleMatch[1]);
  const tokens = [...text.matchAll(codePattern)].map((match) => normalizeCode(match[0]));
  const knownMatch = tokens.find((token) => findDepartmentForCode(token, departments));
  return knownMatch || tokens[0] || "";
}

function extractTeacher(fields: string[]) {
  const text = fields.join(" ");
  const labelled = /(?:teacher|lecturer|dozent|instructor|coach)\s*[:\-]\s*([^,;|]+)/i.exec(text);
  if (labelled) return labelled[1].trim();

  const pipeParts = text.split("|").map((part) => part.trim());
  const likelyName = pipeParts.find((part) => /^[A-Z][a-z]+(?:\s+[A-Z][a-z]+){1,2}$/.test(part));
  return likelyName || "";
}

function startOfToday() {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), now.getDate());
}

function addMonths(date: Date, months: number) {
  return new Date(date.getFullYear(), date.getMonth() + months, date.getDate());
}

function parseSummary(summary: string) {
  const parts = summary.split(",").map((part) => part.trim()).filter(Boolean);
  const modulePart = parts.find((part) => /^Modul\s+/i.test(part));
  const moduleCode = modulePart ? normalizeModuleCode(modulePart.replace(/^Modul\s+/i, "")) : "";
  const topic = parts.find((part) => !/^Unterricht\b/i.test(part) && !/^Modul\s+/i.test(part) && !looksLikePersonName(part)) || "";
  const delivery = detectDelivery(summary);
  return { moduleCode, topic: cleanTopic(topic), delivery };
}

function normalizeModuleCode(value: string) {
  return value.replace(/\s+/g, " ").trim().toUpperCase();
}

function cleanTopic(value: string) {
  return value.replace(/\s+-\s+\((online|hybrid|onCampus)\)/gi, "").trim();
}

function detectDelivery(value: string): LessonEvent["delivery"] {
  if (/\(online\)|\bonline\b/i.test(value)) return "online";
  if (/\(hybrid\)|\bhybrid\b/i.test(value)) return "hybrid";
  if (/\(onCampus\)|\boncampus\b/i.test(value)) return "onCampus";
  return "unknown";
}

function looksLikePersonName(value: string) {
  return /^[A-ZÄÖÜ][a-zäöüß]+(?:\s+[A-ZÄÖÜ][a-zäöüß]+){1,3}$/.test(value);
}
